/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CreateFunctionalSpecPathParameters,
  DeleteFunctionalSpecPathParameters,
  GetFunctionalSpecPathParameters,
  ListFunctionalSpecsParams,
  ListFunctionalSpecsPathParameters,
  TLCreateFunctionalSpecRequest,
  TLFunctionalSpecResponse,
  TLFunctionalSpecsListResponse,
  TLHTTPValidationError,
  TLUploadDocxResponse,
  TLUploadFunctionalSpecDocxProjectsRequest,
  UploadFunctionalSpecDocxParams,
  UploadFunctionalSpecDocxPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Functional Specs
 */
export const listFunctionalSpecs = (
  { projectId }: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFunctionalSpecsListResponse>(
    { url: `/projects/${projectId}/functional-specs`, method: 'GET', params, signal },
    options,
  );
};

export const getListFunctionalSpecsQueryKey = (
  { projectId }: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
) => {
  return ['projects', projectId, 'functional-specs', ...(params ? [params] : [])] as const;
};

export const getListFunctionalSpecsQueryOptions = <
  TData = Awaited<ReturnType<typeof listFunctionalSpecs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFunctionalSpecs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListFunctionalSpecsQueryKey({ projectId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listFunctionalSpecs>>> = ({ signal }) =>
    listFunctionalSpecs({ projectId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listFunctionalSpecs>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListFunctionalSpecsQueryResult = NonNullable<Awaited<ReturnType<typeof listFunctionalSpecs>>>;
export type ListFunctionalSpecsQueryError = ErrorType<TLHTTPValidationError>;

export function useListFunctionalSpecs<
  TData = Awaited<ReturnType<typeof listFunctionalSpecs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListFunctionalSpecsPathParameters,
  params: undefined | ListFunctionalSpecsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFunctionalSpecs>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFunctionalSpecs>>,
          TError,
          Awaited<ReturnType<typeof listFunctionalSpecs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFunctionalSpecs<
  TData = Awaited<ReturnType<typeof listFunctionalSpecs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFunctionalSpecs>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFunctionalSpecs>>,
          TError,
          Awaited<ReturnType<typeof listFunctionalSpecs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFunctionalSpecs<
  TData = Awaited<ReturnType<typeof listFunctionalSpecs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFunctionalSpecs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Functional Specs
 */

export function useListFunctionalSpecs<
  TData = Awaited<ReturnType<typeof listFunctionalSpecs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFunctionalSpecs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListFunctionalSpecsQueryOptions({ projectId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Functional Specs
 */
export const prefetchListFunctionalSpecsQuery = async <
  TData = Awaited<ReturnType<typeof listFunctionalSpecs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listFunctionalSpecs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListFunctionalSpecsQueryOptions({ projectId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Functional Specs
 */
export const invalidateListFunctionalSpecs = async (
  queryClient: QueryClient,
  { projectId }: ListFunctionalSpecsPathParameters,
  params?: ListFunctionalSpecsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListFunctionalSpecsQueryKey({ projectId }, params) }, options);

  return queryClient;
};

/**
 * @summary Create Functional Spec
 */
export const createFunctionalSpec = (
  { projectId }: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFunctionalSpecResponse>(
    {
      url: `/projects/${projectId}/functional-specs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateFunctionalSpecRequest,
      signal,
    },
    options,
  );
};

export const getCreateFunctionalSpecQueryKey = (
  { projectId }: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest?: BodyType<TLCreateFunctionalSpecRequest>,
) => {
  return ['POST', 'projects', projectId, 'functional-specs', tLCreateFunctionalSpecRequest] as const;
};

export const getCreateFunctionalSpecQueryOptions = <
  TData = Awaited<ReturnType<typeof createFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateFunctionalSpecQueryKey({ projectId }, tLCreateFunctionalSpecRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createFunctionalSpec>>> = ({ signal }) =>
    createFunctionalSpec({ projectId }, tLCreateFunctionalSpecRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createFunctionalSpec>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateFunctionalSpecQueryResult = NonNullable<Awaited<ReturnType<typeof createFunctionalSpec>>>;
export type CreateFunctionalSpecQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateFunctionalSpec<
  TData = Awaited<ReturnType<typeof createFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFunctionalSpec>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createFunctionalSpec>>,
          TError,
          Awaited<ReturnType<typeof createFunctionalSpec>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateFunctionalSpec<
  TData = Awaited<ReturnType<typeof createFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFunctionalSpec>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createFunctionalSpec>>,
          TError,
          Awaited<ReturnType<typeof createFunctionalSpec>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateFunctionalSpec<
  TData = Awaited<ReturnType<typeof createFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Functional Spec
 */

export function useCreateFunctionalSpec<
  TData = Awaited<ReturnType<typeof createFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateFunctionalSpecQueryOptions({ projectId }, tLCreateFunctionalSpecRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Functional Spec
 */
export const prefetchCreateFunctionalSpecQuery = async <
  TData = Awaited<ReturnType<typeof createFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateFunctionalSpecQueryOptions({ projectId }, tLCreateFunctionalSpecRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Functional Spec
 */
export const invalidateCreateFunctionalSpec = async (
  queryClient: QueryClient,
  { projectId }: CreateFunctionalSpecPathParameters,
  tLCreateFunctionalSpecRequest: BodyType<TLCreateFunctionalSpecRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateFunctionalSpecQueryKey({ projectId }, tLCreateFunctionalSpecRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Functional Spec
 */
export const deleteFunctionalSpec = (
  { projectId, specId }: DeleteFunctionalSpecPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/projects/${projectId}/functional-specs/${specId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteFunctionalSpecQueryKey = ({ projectId, specId }: DeleteFunctionalSpecPathParameters) => {
  return ['DELETE', 'projects', projectId, 'functional-specs', specId] as const;
};

export const getDeleteFunctionalSpecQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, specId }: DeleteFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteFunctionalSpecQueryKey({ projectId, specId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteFunctionalSpec>>> = ({ signal }) =>
    deleteFunctionalSpec({ projectId, specId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && specId !== null && specId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteFunctionalSpec>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteFunctionalSpecQueryResult = NonNullable<Awaited<ReturnType<typeof deleteFunctionalSpec>>>;
export type DeleteFunctionalSpecQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteFunctionalSpec<
  TData = Awaited<ReturnType<typeof deleteFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteFunctionalSpecPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFunctionalSpec>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteFunctionalSpec>>,
          TError,
          Awaited<ReturnType<typeof deleteFunctionalSpec>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteFunctionalSpec<
  TData = Awaited<ReturnType<typeof deleteFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFunctionalSpec>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteFunctionalSpec>>,
          TError,
          Awaited<ReturnType<typeof deleteFunctionalSpec>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteFunctionalSpec<
  TData = Awaited<ReturnType<typeof deleteFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Functional Spec
 */

export function useDeleteFunctionalSpec<
  TData = Awaited<ReturnType<typeof deleteFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, specId }: DeleteFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteFunctionalSpecQueryOptions({ projectId, specId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Functional Spec
 */
export const prefetchDeleteFunctionalSpecQuery = async <
  TData = Awaited<ReturnType<typeof deleteFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, specId }: DeleteFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteFunctionalSpecQueryOptions({ projectId, specId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Functional Spec
 */
export const invalidateDeleteFunctionalSpec = async (
  queryClient: QueryClient,
  { projectId, specId }: DeleteFunctionalSpecPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteFunctionalSpecQueryKey({ projectId, specId }) }, options);

  return queryClient;
};

/**
 * @summary Get Functional Spec
 */
export const getFunctionalSpec = (
  { projectId, specId }: GetFunctionalSpecPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFunctionalSpecResponse>(
    { url: `/projects/${projectId}/functional-specs/${specId}`, method: 'GET', signal },
    options,
  );
};

export const getGetFunctionalSpecQueryKey = ({ projectId, specId }: GetFunctionalSpecPathParameters) => {
  return ['projects', projectId, 'functional-specs', specId] as const;
};

export const getGetFunctionalSpecQueryOptions = <
  TData = Awaited<ReturnType<typeof getFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, specId }: GetFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFunctionalSpecQueryKey({ projectId, specId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFunctionalSpec>>> = ({ signal }) =>
    getFunctionalSpec({ projectId, specId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && specId !== null && specId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFunctionalSpec>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFunctionalSpecQueryResult = NonNullable<Awaited<ReturnType<typeof getFunctionalSpec>>>;
export type GetFunctionalSpecQueryError = ErrorType<TLHTTPValidationError>;

export function useGetFunctionalSpec<
  TData = Awaited<ReturnType<typeof getFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFunctionalSpecPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFunctionalSpec>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFunctionalSpec>>,
          TError,
          Awaited<ReturnType<typeof getFunctionalSpec>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFunctionalSpec<
  TData = Awaited<ReturnType<typeof getFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFunctionalSpec>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFunctionalSpec>>,
          TError,
          Awaited<ReturnType<typeof getFunctionalSpec>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFunctionalSpec<
  TData = Awaited<ReturnType<typeof getFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Functional Spec
 */

export function useGetFunctionalSpec<
  TData = Awaited<ReturnType<typeof getFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, specId }: GetFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFunctionalSpecQueryOptions({ projectId, specId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Functional Spec
 */
export const prefetchGetFunctionalSpecQuery = async <
  TData = Awaited<ReturnType<typeof getFunctionalSpec>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, specId }: GetFunctionalSpecPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFunctionalSpec>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetFunctionalSpecQueryOptions({ projectId, specId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Functional Spec
 */
export const invalidateGetFunctionalSpec = async (
  queryClient: QueryClient,
  { projectId, specId }: GetFunctionalSpecPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetFunctionalSpecQueryKey({ projectId, specId }) }, options);

  return queryClient;
};

/**
 * @summary Upload Functional Spec Docx
 */
export const uploadFunctionalSpecDocx = (
  { projectId, specId }: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`file`, tLUploadFunctionalSpecDocxProjectsRequest.file);

  return apiInstance<TLUploadDocxResponse>(
    {
      url: `/projects/${projectId}/functional-specs/${specId}/upload`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
      signal,
    },
    options,
  );
};

export const getUploadFunctionalSpecDocxQueryKey = (
  { projectId, specId }: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest?: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
) => {
  return [
    'POST',
    'projects',
    projectId,
    'functional-specs',
    specId,
    'upload',
    ...(params ? [params] : []),
    tLUploadFunctionalSpecDocxProjectsRequest,
  ] as const;
};

export const getUploadFunctionalSpecDocxQueryOptions = <
  TData = Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, specId }: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUploadFunctionalSpecDocxQueryKey({ projectId, specId }, tLUploadFunctionalSpecDocxProjectsRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>> = ({ signal }) =>
    uploadFunctionalSpecDocx(
      { projectId, specId },
      tLUploadFunctionalSpecDocxProjectsRequest,
      params,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && specId !== null && specId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UploadFunctionalSpecDocxQueryResult = NonNullable<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>>;
export type UploadFunctionalSpecDocxQueryError = ErrorType<TLHTTPValidationError>;

export function useUploadFunctionalSpecDocx<
  TData = Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params: undefined | UploadFunctionalSpecDocxParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
          TError,
          Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadFunctionalSpecDocx<
  TData = Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
          TError,
          Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadFunctionalSpecDocx<
  TData = Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Upload Functional Spec Docx
 */

export function useUploadFunctionalSpecDocx<
  TData = Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, specId }: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUploadFunctionalSpecDocxQueryOptions(
    { projectId, specId },
    tLUploadFunctionalSpecDocxProjectsRequest,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Upload Functional Spec Docx
 */
export const prefetchUploadFunctionalSpecDocxQuery = async <
  TData = Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, specId }: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadFunctionalSpecDocx>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUploadFunctionalSpecDocxQueryOptions(
    { projectId, specId },
    tLUploadFunctionalSpecDocxProjectsRequest,
    params,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Upload Functional Spec Docx
 */
export const invalidateUploadFunctionalSpecDocx = async (
  queryClient: QueryClient,
  { projectId, specId }: UploadFunctionalSpecDocxPathParameters,
  tLUploadFunctionalSpecDocxProjectsRequest: BodyType<TLUploadFunctionalSpecDocxProjectsRequest>,
  params?: UploadFunctionalSpecDocxParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getUploadFunctionalSpecDocxQueryKey(
        { projectId, specId },
        tLUploadFunctionalSpecDocxProjectsRequest,
        params,
      ),
    },
    options,
  );

  return queryClient;
};
